import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { isPlaceholderPrice } from '@/lib/format';
import { toast } from 'sonner';

// ============================================================================
// Cart Context — B2B "Mi Cotización"
// Stores items in React state + persists to localStorage so the bag survives
// page reloads and tab close. Provides numeric pricing (unit_price as int CLP),
// quantity controls, and Neto/IVA/Total computation.
// ============================================================================

const STORAGE_KEY = 'allcom_cart_v2';
const IVA_RATE = 0.19;

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

// Parse "$2.490.000" / 2490000 / "2,490,000" → integer CLP
const toIntPrice = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Math.max(0, Math.floor(v));
  const digits = String(v).replace(/[^\d]/g, '');
  const n = parseInt(digits, 10);
  return isNaN(n) ? 0 : Math.max(0, n);
};

const normalizeItem = (raw) => {
  // Accept any "product-like" shape (from solution page, product detail, offers block)
  const priceRaw = raw.price_display || raw.price || raw.unit_price || 0;
  const isPlaceholder = isPlaceholderPrice(priceRaw);
  const unit_price = isPlaceholder ? 0 : toIntPrice(priceRaw);
  const firstImg = Array.isArray(raw.images) && raw.images.length > 0
    ? (typeof raw.images[0] === 'string' ? raw.images[0] : raw.images[0]?.url)
    : null;
  return {
    slug: raw.slug || null,
    name: raw.name || 'Producto sin nombre',
    brand: raw.brand || '',
    category: raw.category || '',
    image: firstImg || raw.image || null,
    unit_price,                    // 0 means "to quote"
    qty: 1,
    model: 'cotizar',              // cotizar | compra | arriendo
  };
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  // Persist on every change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  const addItem = useCallback((product) => {
    const norm = normalizeItem(product);
    setItems((prev) => {
      const existing = prev.find((i) => i.slug && i.slug === norm.slug);
      if (existing) {
        return prev.map((i) => (i.slug === norm.slug ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...prev, norm];
    });
    toast.success(`${norm.name} agregado a tu cotización`);
  }, []);

  const removeItem = useCallback((slug) => setItems((prev) => prev.filter((i) => i.slug !== slug)), []);

  const setQty = useCallback((slug, qty) => {
    const q = Math.max(1, Math.min(999, parseInt(qty, 10) || 1));
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, qty: q } : i)));
  }, []);

  const updateQty = useCallback((slug, delta) => {
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, qty: Math.max(1, Math.min(999, i.qty + delta)) } : i)));
  }, []);

  const updateModel = useCallback((slug, model) => {
    setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, model } : i)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  // Totals derived from items (CLP integer math; rounded only for IVA)
  const totalItems = items.reduce((s, i) => s + (i.qty || 1), 0);
  const neto = items.reduce((s, i) => s + (i.unit_price || 0) * (i.qty || 1), 0);
  const iva = Math.round(neto * IVA_RATE);
  const total = neto + iva;
  const hasRealPrices = neto > 0;

  const value = {
    items, addItem, removeItem, updateQty, setQty, updateModel, clearCart,
    totalItems, neto, iva, total, hasRealPrices,
    open, setOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// ============================================================================
// Cart Drawer (quick preview) — opens from Header bag icon
// ============================================================================
export const CartPanel = () => {
  const { items, removeItem, updateQty, totalItems, neto, hasRealPrices, open, setOpen } = useCart();
  const navigate = useNavigate();

  const goToFullPage = () => {
    setOpen(false);
    navigate('/mi-cotizacion');
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50"
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#0A2540]" strokeWidth={1.6} />
                <h2 className="text-base font-semibold text-[#0A2540]" style={{ fontFamily: 'Syne,sans-serif' }}>
                  Mi cotización ({totalItems})
                </h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="cart-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.length === 0 ? (
                <div className="text-center py-12 text-slate-400" data-testid="cart-empty">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-30" strokeWidth={1.4} />
                  <p className="text-sm">Aún no agregas equipos a tu cotización.</p>
                  <p className="text-[11px] mt-2 text-slate-400">Recorre el catálogo y suma los productos que necesitas.</p>
                </div>
              ) : items.map((item) => (
                <div key={item.slug || item.name} className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`cart-item-${item.slug || 'noslug'}`}>
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover bg-white shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-[10px] text-slate-400 font-semibold shrink-0">{(item.brand || 'ALLCOM').slice(0, 8)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{item.brand}</p>
                    <h4 className="text-sm font-semibold text-[#0A2540] truncate">{item.name}</h4>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(item.slug, -1)} className="w-6 h-6 flex items-center justify-center rounded bg-white border border-slate-200 hover:bg-slate-100" aria-label="Disminuir" data-testid={`cart-qty-decrease-${item.slug || 'noslug'}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-xs font-semibold w-6 text-center tabular-nums">{item.qty}</span>
                        <button onClick={() => updateQty(item.slug, 1)} className="w-6 h-6 flex items-center justify-center rounded bg-white border border-slate-200 hover:bg-slate-100" aria-label="Aumentar" data-testid={`cart-qty-increase-${item.slug || 'noslug'}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.slug)} className="p-1 text-slate-400 hover:text-red-500" aria-label="Eliminar" data-testid={`cart-remove-${item.slug || 'noslug'}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer / CTA */}
            {items.length > 0 && (
              <div className="p-4 border-t border-slate-200 bg-slate-50/60">
                <div className="flex items-center justify-between mb-3 text-sm">
                  <span className="text-slate-500">{hasRealPrices ? 'Neto estimado' : 'Productos'}</span>
                  <span className="font-bold text-[#0A2540] tabular-nums">
                    {hasRealPrices ? '$' + new Intl.NumberFormat('es-CL').format(neto) : `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
                  </span>
                </div>
                <button
                  onClick={goToFullPage}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg transition-all"
                  data-testid="cart-go-to-quote"
                >
                  Ver cotización completa <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-2">Configurarás datos de tu empresa en el siguiente paso.</p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Backwards-compat: legacy CartFAB stays a no-op since the Header now has the bag icon.
// Kept exported in case any old code path still calls it.
export const CartFAB = () => null;
